import {
    Component,
    effect,
    OnDestroy,
    OnInit,
    WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClientSettingsService } from '../../../_shared/services/client-settings.service';
import { RfJwtService } from '../../../_shared/services/rf-jwt.service';
import { Router } from '@angular/router';
import { RFClaims } from '../../../_shared/models/rf-claims';
import { HttpContext } from '@angular/common/http';
import { EchoService } from 'src/app/_shared/services/echo.service';
import type { Channel } from '@ably/laravel-echo';
import { backgroundRequestContext } from '../../../_shared/constants/http-context-tokens';

@Component({
    selector: 'app-kyc-banner',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './kyc-banner.component.html',
})
export class KycBannerComponent implements OnInit, OnDestroy {
    kycChannel: WritableSignal<Channel | undefined> =
        this.echoService.kycChannel;
    channelActive = false;

    show = false;
    routeToUrl = '#';

    jwt: RFClaims | undefined;

    constructor(
        private clientService: ClientSettingsService,
        private echoService: EchoService,
        private _router: Router,
    ) {
        effect(() => {
            if (this.kycChannel() && !this.channelActive) {
                this.channelActive = true;
                this.setupEcho();
            }
            if (!this.kycChannel() && this.channelActive) {
                this.channelActive = false;
            }
        });
    }

    ngOnInit(): void {}

    /**
     * Bound callback stored so ngOnDestroy can pass the same reference to
     * `stopListening` — the channel singleton lives across remounts, and
     * without precise unsubscribe each remount stacks another listener (and
     * therefore another KYC check) on the same Ably channel.
     */
    private onNewKycRequirement = () => {
        const ctx = backgroundRequestContext();
        this.jwt = RfJwtService.getCustomClaims();
        if (!this.jwt) {
            return;
        }
        this.clientService
            .checkClientNeedsKyc(this.jwt.clientId, ctx)
            .subscribe((res: { needsKyc: boolean }) => {
                if (res?.needsKyc) {
                    this.show = true;

                    // go ahead and get url to be ready for them to click
                    this.getKycUrl(ctx);
                }
            });
    };

    /**
     * The exact channel we registered on — EchoService swaps the object when it
     * rebuilds, and unsubscribing from a channel that never saw this callback
     * drops every listener for the event instead of just ours.
     */
    private listeningChannel?: Channel;

    setupEcho() {
        this.listeningChannel = this.kycChannel();
        this.listeningChannel?.listen(
            'NewKYCRequirement',
            this.onNewKycRequirement,
        );
    }

    ngOnDestroy(): void {
        this.listeningChannel?.stopListening(
            'NewKYCRequirement',
            this.onNewKycRequirement,
        );
    }

    getKycUrl(context?: HttpContext) {
        if (this.jwt) {
            this.clientService
                .getClientKycRoute(this.jwt.clientId, context)
                .subscribe({
                    next: (res: { url: string }) => {
                        if (res?.url) {
                            this.routeToUrl = res.url;
                        }
                    },
                    error: () => {
                        this.show = false;
                    },
                });
        }
    }

    // stripe will handle routing back here
    goToStripe() {
        window.location.href = this.routeToUrl;
    }
}
